import { Pagination, Stack, Text } from '@inflearn/ds-react';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { type ReactElement, useEffect } from 'react';
import type {
  ClientApiV1CourseGiftsSentQueryString,
  FindReceivedCourseGiftResponse,
} from '../../../../../resources/@types/courseSchema';
import EmptyStates from '../../../../components/common/EmptyStates';
import { MyGiftsLayout } from '../../../../components/layout/MyPageLayout/MyGiftsLayout';
import { transformGiftStatus } from '../../../../components/pages/my/gifts/common/giftUtils';
import {
  ReceivedGiftItem,
  type ReceivedGiftItemProps,
} from '../../../../components/pages/my/gifts/received/ReceivedGiftItem';
import { COURSE_URL } from '../../../../constants/urls';
import { useCustomQueryParams } from '../../../../hooks/useCustomQueryParams';
import { useCourseGiftsReceivedQuery } from '../../../../queries/useCourseGiftsReceivedQuery';
import { eventNames } from '../../../../utils/GTM/eventNames';
import { useTrackMixpanelEvent } from '../../../../utils/GTM/useTrackMixpanelEvent';
import {
  formatDateToYYYYMDHHMM,
  formatDateToYYYYMDWithUnit,
  formatDateToYYYYMMDD,
} from '../../../../utils/time';

export const getStaticProps = async ({ locale }: { locale: string }) => ({
  props: {
    ...(await serverSideTranslations(locale, ['common'])),
  },
  revalidate: 60,
});

const DEFAULT_QUERY_PARAMS: ClientApiV1CourseGiftsSentQueryString = {
  pageSize: 10,
  pageNumber: 1,
};

function MyReceivedGiftsPage() {
  const { queryParams, push } = useCustomQueryParams<ClientApiV1CourseGiftsSentQueryString>({
    defaultValue: DEFAULT_QUERY_PARAMS,
    queryParamKeys: ['pageNumber', 'pageSize'],
  });
  const { data, isError, isLoading } = useCourseGiftsReceivedQuery({
    params: queryParams,
  });
  const { trackMixpanelEvent, isReady } = useTrackMixpanelEvent();

  const pageNumber = Number(queryParams.pageNumber);

  const isEmpty = data?.items.length === 0;
  const gifts = data?.items.map(transformReceivedGiftItem);
  const totalPage = data?.totalPage ?? 1;

  const isReadyForEvent = isReady && !isLoading && data;

  useEffect(() => {
    if (!isReadyForEvent) {
      return;
    }

    trackMixpanelEvent(
      eventNames.VIEW_RECEIVE_BOX,
      {
        count: data.items.length,
      },
      {
        once: true,
      },
    );
  }, [isReadyForEvent]);

  return (
    <>
      <EmptyStates
        height={300}
        isError={isError}
        isEmpty={isEmpty}
        empty={{
          title: '아직 받은 선물이 없어요',
        }}
      >
        <Stack spacing={20}>
          {gifts?.map((item) => (
            <ReceivedGiftItem key={item.id} item={item} />
          ))}
        </Stack>
      </EmptyStates>
      {totalPage === DEFAULT_QUERY_PARAMS.pageNumber ? null : (
        <Pagination
          value={pageNumber}
          total={totalPage}
          onChange={(page) => push({ pageNumber: page }, { scroll: true })}
          css={{
            marginTop: 56,
            marginBottom: 80,
            justifyContent: 'center',
          }}
        />
      )}
    </>
  );
}

function transformReceivedGiftItem(
  item: FindReceivedCourseGiftResponse['data']['items'][number],
): ReceivedGiftItemProps {
  return {
    id: item.id,
    receivedDate: formatDateToYYYYMMDD(item.approvedAtUTC),
    courseTitle: item.orderTitle,
    courseDetailURL: COURSE_URL.COURSE_DETAIL(item.courseSlug),
    senderName: item.senderName,
    courseDescription01: getDescription01ByStatus(item),
    courseDescription02: getDescription02ByStatus(item),
    status: transformGiftStatus(item.status),
    courseImageUrl: item.giftPhotoCardImageUrl,
    detailURL: COURSE_URL.GIFTS_RECEIVED_DETAIL({ code: item.code }),
  };
}

function getDescription01ByStatus({
  canceledAtUTC,
  duration,
}: FindReceivedCourseGiftResponse['data']['items'][number]) {
  if (canceledAtUTC) {
    return (
      <Text size="sm" color="gray.7">
        {formatDateToYYYYMDHHMM(canceledAtUTC)} 취소됨
      </Text>
    );
  }

  if (duration) {
    return (
      <Text size="sm" color="gray.7">
        수강 기한 {`${duration}개월`}
      </Text>
    );
  }
  if (duration === null) {
    return (
      <Text size="sm" color="gray.7">
        수강 기한 무제한
      </Text>
    );
  }

  // if (isDeletedCourse) {
  // TODO: 강의 상태값 필요
  // <Text size="sm" color="red.7">
  //   수강할 수 없는 강의입니다.
  // </Text>
  // }

  return <></>;
}

function getDescription02ByStatus({
  status,
  limitedAtUTC,
}: FindReceivedCourseGiftResponse['data']['items'][number]) {
  if (status === 'CANCELED') {
    return '';
  }

  if (limitedAtUTC) {
    return (
      <Text size="sm" color="gray.7">
        {formatDateToYYYYMDWithUnit(limitedAtUTC)}까지 수강 가능
      </Text>
    );
  }

  return '';
}

MyReceivedGiftsPage.getLayout = (page: ReactElement, pathname: string) => (
  <MyGiftsLayout
    pathname={pathname}
    title="메타.받은선물함.제목"
    description="메타.받은선물함.설명"
  >
    {page}
  </MyGiftsLayout>
);

export default MyReceivedGiftsPage;
